/**
 * Camera-orientation vocabulary for view modes.
 *
 * Views whose derived {@link OrientationKey} is EQUAL share camera identity:
 * switching between them must never write to the camera (pose and heading
 * carry over verbatim — "light switch"). Views with different keys realign
 * on entry per their class rules.
 *
 * This decouples camera identity from `IViewMode.id`: `xray_2d`,
 * `orthographic_2d_top` and `plan_2d_skeleton` are distinct view ids
 * (different shading/density/background) but the same top-down camera, while
 * `xray_2d_surface` / `xray_2d_wall` look identical to X-Ray on screen yet
 * have tool-managed plane-aligned cameras.
 */

import { CameraMode } from '../defines';
import type { IViewMode } from './types';

/** Static camera identity class of a view mode. */
export type CameraProfile =
  /** Free 3D perspective camera; heading axis is yaw. */
  | 'perspective_3d'
  /** Top-down orthographic (map / X-Ray / plan); heading axis is roll. */
  | 'top_down_2d'
  /** Side profile orthographic; keeps its own orientation. */
  | 'side_2d'
  /** Plane/surface/wall-aligned orthographic; camera pose is derived from
   *  the plane (or set by the activating tool). Context-dependent. */
  | 'plane_2d';

/**
 * Full camera identity = profile + plane/surface context. `plane_2d` is the
 * only context-dependent profile: two plane views share identity only when
 * they target the same plane. A `plane_2d:` key with an empty context means
 * "tool-managed plane camera" (e.g. xray_2d_surface without a viewContext) —
 * such views are never camera-realigned by the ViewManager.
 */
export type OrientationKey = Exclude<CameraProfile, 'plane_2d'> | `plane_2d:${string}`;

/**
 * Derive the orientation key for a view mode. Views that declare
 * `cameraProfile` use it directly; undeclared views fall back to inference
 * from cameraMode/planeId (reproducing the legacy
 * CameraSettingsManager.getCameraSettingTypeFromViewMode bucketing), so
 * adoption can be incremental.
 */
export function orientationKeyForView(view: Pick<IViewMode, 'cameraProfile' | 'cameraMode' | 'viewContext'> | null | undefined): OrientationKey | null {
  if (!view) return null;
  const profile = view.cameraProfile ?? inferProfile(view);
  if (profile === 'plane_2d') {
    return `plane_2d:${view.viewContext?.planeId ?? ''}`;
  }
  return profile;
}

function inferProfile(view: Pick<IViewMode, 'cameraMode' | 'viewContext'>): CameraProfile {
  if (view.cameraMode !== CameraMode.ORTHOGRAPHIC) {
    return 'perspective_3d';
  }
  const planeId = view.viewContext?.planeId;
  if (planeId === 'side') {
    return 'side_2d';
  }
  if (!planeId || planeId === 'map') {
    return 'top_down_2d';
  }
  return 'plane_2d';
}

/**
 * Whether two views share camera identity — the invariant behind
 * preserve-not-restore: same key ⇒ zero camera writes during the switch.
 * Null on either side (no previous view) is never "same".
 */
export function sameOrientation(a: Pick<IViewMode, 'cameraProfile' | 'cameraMode' | 'viewContext'> | null | undefined, b: Pick<IViewMode, 'cameraProfile' | 'cameraMode' | 'viewContext'> | null | undefined): boolean {
  const keyA = orientationKeyForView(a);
  const keyB = orientationKeyForView(b);
  return keyA !== null && keyA === keyB;
}

/** Whether the key is the top-down class whose roll carries the user's floor rotation. */
export function isTopDownKey(key: OrientationKey | null): boolean {
  return key === 'top_down_2d';
}

/**
 * Which `View` axis carries the screen heading for a class. At pitch −90°
 * yaw and roll are gimbal-degenerate; the convention is: 2D classes express
 * heading as roll (yaw pinned), perspective 3D as yaw (roll pinned to 0).
 */
export function headingAxis(key: OrientationKey): 'roll' | 'yaw' {
  return key === 'perspective_3d' ? 'yaw' : 'roll';
}
